import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import SupplyModalSimple from './SupplyModalSimple';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Package, 
  Plus, 
  Clock, 
  CheckCircle,
  Coffee,
  Cake,
  Snowflake,
  Truck,
  Package2
} from 'lucide-react';

const ProductionWorking = () => {
  const { user, API } = useContext(UserContext);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCounter, setSelectedCounter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [recentEntries, setRecentEntries] = useState([]);
  const [stockAvailability, setStockAvailability] = useState([]);
  const [showSupplyModal, setShowSupplyModal] = useState(false);

  const COUNTERS = [
    { id: 'pain', name: 'Pains', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'viennoiserie', name: 'Viennoiserie', color: 'bg-purple-100 text-purple-800' },
    { id: 'gateaux', name: 'Gâteaux', color: 'bg-pink-100 text-pink-800' }
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading production data...');
        setDataLoaded(false);
        
        const productsResponse = await axios.get(`${API}/products`);
        setAllProducts(productsResponse.data || []);
        
        const entriesResponse = await axios.get(`${API}/production?date=${new Date().toISOString().split('T')[0]}`);
        setRecentEntries(entriesResponse.data?.slice(0, 10) || []);
        
        const stockResponse = await axios.get(`${API}/stock/availability`);
        setStockAvailability(stockResponse.data || []);
        
        setDataLoaded(true);
        console.log('Production data loaded successfully');
        
      } catch (error) {
        console.error('Error loading production data:', error);
        toast.error('Erreur de chargement');
        setAllProducts([]);
        setRecentEntries([]);
        setDataLoaded(true);
      }
    };
    
    if (user) {
      loadData();
    }
  }, [user, API]);

  const selectCounter = (counterId) => {
    setSelectedCounter(counterId);
    setSelectedProduct('');
    setQuantity('');
  };

  const getProductsForCounter = (counterId) => {
    if (!counterId || !Array.isArray(allProducts)) return [];
    return allProducts.filter(product => product && product.counter === counterId);
  };

  const getProductInfo = (productId) => {
    if (!productId || !Array.isArray(allProducts)) return null;
    return allProducts.find(p => p && p.id === productId) || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedProduct || !quantity || parseFloat(quantity) <= 0) {
        toast.error('Veuillez remplir tous les champs');
        return;
      }

      setLoading(true);

      await axios.post(`${API}/production`, {
        product_id: selectedProduct,
        quantity_produced: parseFloat(quantity),
        user_id: user.id
      });

      toast.success('Production enregistrée !');
      
      setSelectedProduct('');
      setQuantity('');
      
      const entriesResponse = await axios.get(`${API}/production?date=${new Date().toISOString().split('T')[0]}`);
      setRecentEntries(entriesResponse.data?.slice(0, 10) || []);
      
    } catch (error) {
      console.error('Production submission error:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      if (!window.confirm('Supprimer cet enregistrement ?')) return;
      
      await axios.delete(`${API}/production/${entryId}?user_role=${user.role}`);
      toast.success('Supprimé !');
      
      const entriesResponse = await axios.get(`${API}/production?date=${new Date().toISOString().split('T')[0]}`);
      setRecentEntries(entriesResponse.data?.slice(0, 10) || []);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur de suppression');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '--:--';
    }
  };

  const canManage = () => {
    return user && ['manager', 'owner', 'system_admin'].includes(user.role);
  };

  if (!dataLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement de la production...</p>
          </div>
        </div>
      </div>
    );
  }

  const counterProducts = getProductsForCounter(selectedCounter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col lg:flex-row justify-between items-start lg:items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Saisie de Production
            </h1>
            <p className="text-gray-600">
              Enregistrez la production quotidienne
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-4 lg:mt-0">
            {/* Stock Indicator */}
            <div className="bg-white/70 glass rounded-lg p-3 border border-gray-200">
              <div className="flex items-center gap-2">
                <Package2 className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">Stock disponible:</span>
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">
                  {stockAvailability.length} produit(s)
                </Badge>
              </div>
            </div>
            
            {/* Supply Button */}
            <Button
              onClick={() => {
                if (!selectedCounter) {
                  toast.warning('Veuillez d\'abord sélectionner un guichet');
                  return;
                }
                setShowSupplyModal(true);
              }}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-lg btn-hover"
              data-testid="supply-btn"
            >
              <Truck className="h-4 w-4 mr-2" />
              Approvisionnement du Jour
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulaire Production */}
          <Card className="bg-white/90 border shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Nouvelle Production
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Sélection Guichet */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">
                    Guichet
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {COUNTERS.map((counter) => (
                      <button
                        key={counter.id}
                        type="button"
                        onClick={() => selectCounter(counter.id)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedCounter === counter.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                        data-testid={`counter-${counter.id}`}
                      >
                        {counter.id === 'pain' && (
                          <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                        )}
                        {counter.id === 'viennoiserie' && (
                          <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                        )}
                        {counter.id === 'gateaux' && (
                          <Cake className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                        )}
                        <p className="text-sm font-medium">{counter.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sélection Produit */}
                {selectedCounter && counterProducts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Produit ({counterProducts.length} disponible(s))
                    </Label>
                    
                    <select
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                      data-testid="product-select"
                    >
                      <option value="">Sélectionnez un produit</option>
                      {counterProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {product.price} F CFA
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quantité */}
                {selectedProduct && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Quantité produite
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="h-12"
                      placeholder="Ex: 50"
                      data-testid="quantity-input"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !selectedProduct || !quantity}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg shadow"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Productions Récentes */}
          <Card className="bg-white/90 border shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-600" />
                Productions Récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentEntries.length > 0 ? recentEntries.map((entry, index) => {
                  if (!entry?.id) return null;
                  
                  const product = getProductInfo(entry.product_id);
                  
                  return (
                    <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-full bg-blue-100">
                          <Package className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {product?.name || 'Produit inconnu'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatTime(entry.time)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800">
                          {entry.quantity_produced || 0}
                        </Badge>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        {canManage() && (
                          <button
                            onClick={() => deleteEntry(entry.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Supprimer"
                          >
                            <span className="text-red-500">✖</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }).filter(Boolean) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Aucune production aujourd'hui</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <Card className="mt-8 bg-white/90 border shadow-lg">
          <CardHeader>
            <CardTitle>Actions Rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {COUNTERS.map((counter) => (
                <button
                  key={counter.id}
                  onClick={() => selectCounter(counter.id)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    selectedCounter === counter.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {counter.id === 'pain' && (
                    <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                  )}
                  {counter.id === 'viennoiserie' && (
                    <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                  )}
                  {counter.id === 'gateaux' && (
                    <Cake className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                  )}
                  <p className="text-sm font-medium">{counter.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {allProducts.filter(p => p && p.counter === counter.id).length} produits
                  </p>
                </button>
              ))}
            </div>

            {/* Mode Viennoiserie */}
            <div className="border-t border-gray-200 pt-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-purple-800 flex items-center gap-2">
                      <Coffee className="h-4 w-4" />
                      🥐 Mode Production Viennoiserie Avancé
                    </h4>
                    <p className="text-sm text-purple-700 mt-1">
                      Fabrication → Mini-stock Frigo → Cuisson → Guichet
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      try {
                        window.location.href = '/production/viennoiserie';
                      } catch (error) {
                        toast.info('Mode viennoiserie sera bientôt disponible');
                      }
                    }}
                    className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-medium shadow-lg"
                    data-testid="viennoiserie-mode-btn"
                  >
                    <Snowflake className="h-4 w-4 mr-2" />
                    Mode Viennoiserie
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Modal d'Approvisionnement */}
      <SupplyModalSimple 
        isOpen={showSupplyModal}
        onClose={() => setShowSupplyModal(false)}
        selectedCounter={selectedCounter}
      />
      
      <Footer />
    </div>
  );
};

export default ProductionWorking;